import React from 'react'
import { useNavigate } from 'react-router-dom'
import useSessionStore from '../store/sessionStore'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import styles from './Preview.module.css'

export default function Preview() {
  const navigate = useNavigate()
  const { capturedFiles, lastCapturedFileId } = useSessionStore()

  const capturedFile = capturedFiles.find((f) => f.id === lastCapturedFileId) || capturedFiles[capturedFiles.length - 1]

  if (!capturedFile) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <h1 className={styles.title}>No file to preview</h1>
          <p className={styles.sub}>Please capture or upload a file first.</p>
          <Button onClick={() => navigate('/capture')}>Go to capture</Button>
        </div>
      </main>
    )
  }

  const isVideo = capturedFile.type === 'video'
  const bannerText = isVideo
    ? 'Make sure your face is clearly visible and you followed all instructions.'
    : capturedFile.type === 'id'
    ? 'Ensure all text is legible and all four corners are visible.'
    : 'Ensure your full face is visible with good lighting.'

  const handleContinue = () => {
    navigate('/finish')
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <h1 className={styles.title}>Preview</h1>
        <p className={styles.sub}>Review your file before continuing.</p>

        <div className={styles.banner}>
          <p>{bannerText}</p>
        </div>

        <div className={styles.mediaWrap}>
          {isVideo ? (
            <video
              src={capturedFile.previewUrl}
              controls
              className={styles.media}
              aria-label="Video preview"
            />
          ) : (
            <img
              src={capturedFile.previewUrl}
              alt="Document preview"
              className={styles.media}
            />
          )}
        </div>

        <Card padding="md" className={styles.details}>
          <h2 className={styles.detailsTitle}>File details</h2>
          <dl className={styles.detailsList}>
            <div className={styles.detailRow}>
              <dt>Name</dt>
              <dd>{capturedFile.originalName}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt>Type</dt>
              <dd>{capturedFile.mimeType}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt>Size</dt>
              <dd>{(capturedFile.sizeBytes / 1024 / 1024).toFixed(2)} MB</dd>
            </div>
            <div className={styles.detailRow}>
              <dt>Method</dt>
              <dd>{capturedFile.method === 'webcam' ? 'Webcam capture' : 'File upload'}</dd>
            </div>
          </dl>
        </Card>

        <Card padding="md" className={styles.encryption}>
          <div className={styles.encryptionHeader}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h2 className={styles.encryptionTitle}>End-to-end encryption</h2>
          </div>
          <p className={styles.encryptionText}>
            Your file will be encrypted with AES-256-GCM in your browser before upload.
            The encryption key stays on your device. Our server only stores ciphertext
            it cannot read.
          </p>
        </Card>

        <div className={styles.actions}>
          <Button
            variant="ghost"
            onClick={() => navigate('/capture')}
          >
            Back
          </Button>
          <Button onClick={handleContinue}>Continue</Button>
        </div>
      </div>
    </main>
  )
}
